import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, addDoc, updateDoc, doc, query, getDocs, Timestamp, getDoc, deleteDoc, writeBatch, where, setDoc } from 'firebase/firestore';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { X, Clock, Calendar, User, ChevronLeft, ChevronRight, Plus, AlertTriangle, Copy, RotateCcw, Search, Shield, Check, XCircle, AlertCircle } from 'lucide-react';
import ClientAutocomplete from './ClientAutocomplete';
import StandardAddAppointmentModal from './StandardAddAppointmentModal';
import EnhancedEditAppointmentModal from './EnhancedEditAppointmentModal';
import EnhancedAppointmentDetailsModal from './EnhancedAppointmentDetailsModal';
import { useTheme } from '../contexts/ThemeContext';

const MonthView = ({ db, currentDate, currentUser, showCompletedAppointments, filterText }) => {
  const { theme } = useTheme();

  // --- STATES ---
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [firstDayOfWeek, setFirstDayOfWeek] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [adminRoles, setAdminRoles] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [clients, setClients] = useState([]);
  const [clickTimer, setClickTimer] = useState(null);
  const clickDelay = 200;
  const [userPermissions, setUserPermissions] = useState({
    read: false, write: false, create: false, delete: false, manage: false
  });
  const [expandedDays, setExpandedDays] = useState(new Set());
  const maxVisibleAppointments = 3;

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  // --- PERMISSIONS ---
  const fetchUserPermissions = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRoleId = userData.role;
        try {
          const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', userRoleId));
          if (advancedRoleDoc.exists()) {
            const p = advancedRoleDoc.data().permissions?.appointments || {};
            setUserPermissions({
              read: p.read || false, write: p.write || false, create: p.create || false, delete: p.delete || false, manage: p.manage || false
            });
            return;
          }
        } catch (e) { }
        const roleDoc = await getDoc(doc(db, 'roles', userRoleId));
        if (roleDoc.exists()) {
          const p = roleDoc.data().permissions;
          setUserPermissions({
            read: p.includes('read_appointments'),
            write: p.includes('write_appointments'),
            create: p.includes('write_appointments'),
            delete: p.includes('write_appointments'),
            manage: p.includes('write_appointments')
          });
        }
      }
    } catch (e) { console.error(e); }
  }, [db, currentUser]);
  useEffect(() => { fetchUserPermissions(); }, [fetchUserPermissions]);

  const canViewAll = useMemo(() => userPermissions.manage && !isMobile, [userPermissions.manage, isMobile]);
  const canModify = useMemo(() => userPermissions.write, [userPermissions.write]);
  const canCreate = useMemo(() => userPermissions.create, [userPermissions.create]);

  // --- DATA ---
  const fetchRoles = useCallback(async () => {
    if (!db) return;
    const s = await getDocs(collection(db, 'roles'));
    setAdminRoles(s.docs.filter(d => d.data().permissions?.includes('admin_access')).map(d => d.id));
  }, [db]);
  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const q = query(collection(db, 'employees'), where('isArchived', '==', false));
      const [snap, userDoc] = await Promise.all([
        getDocs(q),
        currentUser ? getDoc(doc(db, 'employees', currentUser.userId)) : Promise.resolve(null)
      ]);
      const map = new Map();
      snap.forEach(d => map.set(d.id, { userId: d.id, ...d.data(), color: d.data().color || '#808080', isAdmin: adminRoles.includes(d.data().role) }));
      if (userDoc?.exists()) {
        const d = userDoc.data();
        map.set(userDoc.id, { userId: userDoc.id, ...d, color: d.color || '#808080', isAdmin: adminRoles.includes(d.role) });
      }

      const list = Array.from(map.values()).filter(e => {
        if (currentUser && e.userId === currentUser.userId) return true;
        if (e.role === 'beneficiaire') return false;
        return e.isActive !== false && !e.isArchived;
      });
      setEmployees(list);

      // Check current user admin status
      if (currentUser) {
        const me = list.find(e => e.userId === currentUser.userId);
        setIsAdmin(me?.isAdmin || false);
      }
    } catch (e) { console.error(e); }
  }, [db, adminRoles, currentUser]);
  useEffect(() => { fetchEmployees(); }, [fetchEmployees, adminRoles]); // Removed condition adminRoles.length > 0 to ensure load

  const visibleEmployees = useMemo(() => {
    if (isMobile) return employees.filter(e => e.userId === currentUser.userId);
    if (isAdmin || canViewAll) return employees;
    return employees.filter(e => e.userId === currentUser.userId);
  }, [employees, isAdmin, canViewAll, isMobile, currentUser]);

  const visibleEmployeeIds = useMemo(() => visibleEmployees.map(e => e.userId), [visibleEmployees]);

  // Filtrer les rendez-vous (Terminés + Recherche Globale)
  const filteredAppointments = useMemo(() => {
    return appointments.filter(appointment => {
      // 1. Filtre Terminés
      const isCompleted = appointment.status === 'completed';
      const shouldShow = showCompletedAppointments || !isCompleted;

      if (!shouldShow) return false;

      // 2. Filtre Recherche Globale
      if (filterText) {
        const lowerFilter = filterText.toLowerCase();
        const clientName = appointment.clientName ? appointment.clientName.toLowerCase() : '';

        let employeeName = '';
        if (appointment.employeeId) {
          const emp = employees.find(e => e.userId === appointment.employeeId);
          if (emp) {
            employeeName = (emp.name || `${emp.firstName} ${emp.lastName}` || '').toLowerCase();
          }
        }

        return clientName.includes(lowerFilter) || employeeName.includes(lowerFilter);
      }

      return true;
    });
  }, [appointments, showCompletedAppointments, filterText, employees]);

  const fetchAppointments = useCallback(async () => {
    if (!db) return;
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
    // Pour couvrir les débordements de calendrier (précédent/suivant mois)
    const visibleStart = new Date(start);
    visibleStart.setDate(visibleStart.getDate() - 7);
    const visibleEnd = new Date(end);
    visibleEnd.setDate(visibleEnd.getDate() + 14);

    const q = query(
      collection(db, 'appointments'),
      where('date', '>=', Timestamp.fromDate(visibleStart)),
      where('date', '<=', Timestamp.fromDate(visibleEnd))
    );
    try {
      const snap = await getDocs(q);
      const apps = await Promise.all(snap.docs.map(async d => {
        const data = d.data();
        // Fetch client info if needed (optim: cache)
        let cName = 'Client';
        let cColor = '#808080';
        if (data.clientId) {
          // Simple mock or fetch if clients state populated
          const client = clients.find(c => c.id === data.clientId);
          if (client) { cName = `${client.lastName} ${client.firstName}`; cColor = client.color; }
        }
        return {
          id: d.id, ...data,
          date: data.date?.toDate() || new Date(),
          clientName: cName,
          clientColor: cColor
        };
      }));
      setAppointments(apps);
    } catch (e) { console.error(e); }
  }, [db, currentDate, clients]); // Added clients dep

  useEffect(() => {
    const fetchClients = async () => {
      if (!db) return;
      const s = await getDocs(query(collection(db, 'clients'), where('isActive', '==', true)));
      setClients(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchClients();
  }, [db]);

  useEffect(() => { if (clients.length > 0) fetchAppointments(); }, [db, currentDate, clients, fetchAppointments]); // Wait for clients

  // --- CALENDAR GRID ---
  const calendarDates = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const dates = [];
    const startPadding = (firstDay.getDay() - firstDayOfWeek + 7) % 7;

    for (let i = 0; i < startPadding; i++) {
      dates.unshift(new Date(year, month, -i));
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      dates.push(new Date(year, month, i));
    }
    const endPadding = 42 - dates.length; // 6 rows max logic
    for (let i = 1; i <= endPadding; i++) {
      dates.push(new Date(year, month + 1, i));
    }
    return dates;
  }, [currentDate, firstDayOfWeek]);

  const getDayNames = () => {
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const rotated = [...days];
    if (firstDayOfWeek === 1) { const sun = rotated.shift(); rotated.push(sun); }
    return rotated;
  };

  // --- INTERACTIONS ---
  const handleCellClick = (date) => {
    if (!canCreate) return;
    setSelectedDate(date);
    // Default to current user or first visible
    setSelectedEmployee(currentUser.userId);
    setShowAddModal(true);
  };

  const handleEventClick = (app, e) => {
    e.stopPropagation();
    if (clickTimer) {
      clearTimeout(clickTimer);
      setClickTimer(null);
      // Double click
      if (canModify) {
        setSelectedAppointment(app);
        setShowEditModal(true);
      }
    } else {
      setClickTimer(setTimeout(() => {
        setSelectedAppointment(app);
        setShowDetailsModal(true);
        setClickTimer(null);
      }, clickDelay));
    }
  };

  const toggleDayExpansion = (dateStr) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
      return next;
    });
  };

  const isSameDay = (d1, d2) => d1.getDate() === d2.getDate() && d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();

  // --- DRAG AND DROP ---
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || !canModify) return;
    if (source.droppableId === destination.droppableId) return;

    const destDateTimestamp = parseInt(destination.droppableId);
    if (isNaN(destDateTimestamp)) return;
    const destDate = new Date(destDateTimestamp);

    const appointment = appointments.find(app => app.id === draggableId);
    if (!appointment) return;

    try {
      // Conserver l'heure
      const newDate = new Date(destDate);
      newDate.setHours(appointment.date.getHours(), appointment.date.getMinutes());

      // Optimistic UI ? Or just fetch. Fetch is safer.
      // Update doc
      await updateDoc(doc(db, 'appointments', appointment.id), {
        date: Timestamp.fromDate(newDate)
      });
      fetchAppointments();

    } catch (e) {
      console.error(e);
    }
  };


  // --- RENDER HELPERS ---
  const getEventStyle = (app) => {
    const base = "px-1.5 py-0.5 rounded text-[10px] mb-1 cursor-pointer border-l-2 truncate transition shadow-sm hover:shadow-md";
    // Status colors
    if (!app.employeeId || app.status === 'libre') return `${base} bg-red-100 dark:bg-red-900/30 border-red-500 text-red-800 dark:text-red-200`;
    if (app.status === 'reserve') return `${base} bg-amber-100 dark:bg-amber-900/30 border-amber-500 text-amber-800 dark:text-amber-200`;
    if (app.status === 'completed') return `${base} bg-gray-100 dark:bg-gray-800 border-gray-400 dark:border-gray-500 text-gray-600 dark:text-gray-400 line-through opacity-70`;

    // Assigne / In Progress
    return {
      style: {
        backgroundColor: app.clientColor ? `${app.clientColor}33` : undefined, // ~20% opacity
        borderLeftColor: app.clientColor || '#6b7280',
        // color removed to let className handle it
      },
      className: `${base} text-gray-900 dark:text-gray-100 hover:brightness-95 bg-gray-50 dark:bg-gray-700` // Fallback bg if no client color
    };
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
      {/* HEADER JOURS */}
      <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm z-10">
        {getDayNames().map((d, i) => (
          <div key={i} className="py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            {d}
          </div>
        ))}
      </div>

      {/* GRILLE CALENDRIER */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex-1 grid grid-cols-7 grid-rows-6 bg-gray-200 dark:bg-gray-700 gap-px overflow-y-auto">
          {calendarDates.map((date, idx) => {
            const dateStr = date.toISOString().split('T')[0];
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const isToday = isSameDay(date, new Date());

            const dayApps = filteredAppointments.filter(a =>
              isSameDay(a.date, date) &&
              (visibleEmployeeIds.includes(a.employeeId) || !a.employeeId) // Show visible employees + unassigned
            );
            // Sort by time
            dayApps.sort((a, b) => a.startTime.localeCompare(b.startTime));

            const isExpanded = expandedDays.has(dateStr);
            const displayApps = isExpanded ? dayApps : dayApps.slice(0, maxVisibleAppointments);
            const hiddenCount = dayApps.length - displayApps.length;

            const droppableId = date.getTime().toString();

            return (
              <Droppable key={droppableId} droppableId={droppableId}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`
                                  relative flex flex-col p-1 min-h-[80px] transition-colors
                                  ${isCurrentMonth ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50 text-gray-400'}
                                  ${isToday ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}
                                  ${snapshot.isDraggingOver ? 'bg-blue-100 dark:bg-blue-800/40 ring-inset ring-2 ring-blue-500/50' : ''}
                                  hover:bg-gray-50 dark:hover:bg-gray-800
                              `}
                    onClick={() => handleCellClick(date)}
                  >
                    {/* Date Number */}
                    <div className={`
                                  self-end w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium mb-1
                                  ${isToday ? 'bg-blue-600 text-white shadow-md' : 'text-gray-700 dark:text-gray-300'}
                              `}>
                      {date.getDate()}
                    </div>

                    {/* Events List */}
                    <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                      {displayApps.map((app, index) => {
                        const styleObj = getEventStyle(app);
                        const isObj = typeof styleObj === 'object';
                        return (
                          <Draggable key={app.id} draggableId={app.id} index={index} isDragDisabled={!canModify}>
                            {(providedDrag, snapshotDrag) => (
                              <div
                                ref={providedDrag.innerRef}
                                {...providedDrag.draggableProps}
                                {...providedDrag.dragHandleProps}
                                className={`${isObj ? styleObj.className : styleObj} ${snapshotDrag.isDragging ? 'opacity-50 scale-105 shadow-xl rotate-2 z-50' : ''}`}
                                style={isObj ? { ...providedDrag.draggableProps.style, ...styleObj.style } : providedDrag.draggableProps.style}
                                onClick={(e) => handleEventClick(app, e)}
                                title={`${app.startTime} - ${app.clientName} (${app.employeeName || employees.find(e => e.userId === app.employeeId)?.name || '?'})`}
                              >
                                <div className="flex flex-col leading-none py-0.5">
                                  <div className="flex items-center gap-1">
                                    <span className="font-mono text-[9px] opacity-75">{app.startTime}</span>
                                    <span className="font-semibold truncate flex-1">{app.clientName}</span>
                                  </div>
                                  <div className="text-[9px] opacity-80 truncate pl-1 flex items-center gap-1 mt-0.5">
                                    <User size={8} />
                                    <span>{app.employeeName || employees.find(e => e.userId === app.employeeId)?.name || employees.find(e => e.userId === app.employeeId)?.firstName || 'Non assigné'}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}

                      {/* Show More */}
                      {hiddenCount > 0 && !isExpanded && (
                        <div
                          className="text-[10px] text-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded py-0.5 cursor-pointer font-medium"
                          onClick={(e) => { e.stopPropagation(); toggleDayExpansion(dateStr); }}
                        >
                          + {hiddenCount} autres
                        </div>
                      )}
                      {isExpanded && hiddenCount <= 0 && dayApps.length > maxVisibleAppointments && (
                        <div
                          className="text-[10px] text-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded py-0.5 cursor-pointer font-medium"
                          onClick={(e) => { e.stopPropagation(); toggleDayExpansion(dateStr); }}
                        >
                          Voir moins
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>

      {/* MODALS */}
      {showDetailsModal && selectedAppointment && (
        <EnhancedAppointmentDetailsModal
          appointment={selectedAppointment}
          onClose={() => setShowDetailsModal(false)}
          onUpdate={fetchAppointments}
          onEdit={() => { setShowDetailsModal(false); setShowEditModal(true); }}
          onDelete={async (id) => {
            try {
              await deleteDoc(doc(db, 'appointments', id));
              fetchAppointments();
              setShowDetailsModal(false);
            } catch (e) { console.error(e); }
          }}
          canEdit={canModify}
          db={db}
        />
      )}

      {showAddModal && (
        <StandardAddAppointmentModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={async (data) => {
            await addDoc(collection(db, 'appointments'), { ...data, date: Timestamp.fromDate(data.date) });
            fetchAppointments();
            setShowAddModal(false);
          }}
          prefilledDate={selectedDate}
          prefilledEmployeeId={selectedEmployee}
          employees={employees}
          clients={clients}
        />
      )}

      {showEditModal && selectedAppointment && (
        <EnhancedEditAppointmentModal
          appointment={selectedAppointment}
          employees={employees}
          clients={clients}
          onClose={() => setShowEditModal(false)}
          onSave={async (data) => {
            await updateDoc(doc(db, 'appointments', selectedAppointment.id), { ...data, date: Timestamp.fromDate(data.date) });
            fetchAppointments();
            setShowEditModal(false);
          }}
          onDelete={async (id) => {
            await deleteDoc(doc(db, 'appointments', id));
            fetchAppointments();
            setShowEditModal(false);
          }}
          canDelete={canModify}
        />
      )}
    </div>
  );
};

export default MonthView;
